// Global notification dispatcher for integrating toast notifications with the notification center

import i18n from '@/i18n';

let notificationCenterInstance = null;

// localStorage persistence
const NOTIFICATIONS_STORAGE_KEY = 'mbility_notifications';
const MAX_STORED_NOTIFICATIONS = 100;

// dismissed ids persistence (per evitare che tornino dopo refresh/rehydrate)
const DISMISSED_STORAGE_KEY = 'mbility_notifications_dismissed';
const MAX_DISMISSED_IDS = 1000;

export const saveNotificationsToStorage = (notifications) => {
  try {
    const list = Array.isArray(notifications) ? notifications : [];
    const filteredNotifications = list
      .filter((n) => n?.containerId === 'dashboard')
      .slice(-MAX_STORED_NOTIFICATIONS);

    // ✅ salva SEMPRE (anche [])
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(filteredNotifications));
  } catch (error) {
    console.error('Error saving notifications to localStorage:', error);
  }
};

export const loadNotificationsFromStorage = () => {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error loading notifications from localStorage:', error);
    return [];
  }
};

export const loadDismissedNotificationIds = () => {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    console.error('Error loading dismissed ids:', e);
    return [];
  }
};

export const addDismissedNotificationId = (id) => {
  if (!id) return;
  try {
    const current = loadDismissedNotificationIds();
    if (current.includes(id)) return;
    const next = [...current, id].slice(-MAX_DISMISSED_IDS);
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    console.error('Error saving dismissed id:', e);
  }
};

export const addDismissedNotificationIds = (ids) => {
  try {
    const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (list.length === 0) return;

    const current = loadDismissedNotificationIds();
    const set = new Set(current);

    for (const id of list) set.add(id);

    const next = Array.from(set).slice(-MAX_DISMISSED_IDS);
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    console.error('Error saving dismissed ids:', e);
  }
};

export const clearDismissedNotificationIds = () => {
  try {
    localStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch (e) {
    console.error('Error clearing dismissed ids:', e);
  }
};

export const setNotificationCenterInstance = (instance) => {
  notificationCenterInstance = instance;
};

export const addDriverWarningNotification = (warning, driverName) => {
  try {
    if (notificationCenterInstance && typeof notificationCenterInstance.add === 'function') {
      const timestamp = Date.now();
      const dateMs = warning.data_segnalazione
        ? new Date(warning.data_segnalazione).getTime()
        : timestamp;
      const id = `warning-${warning.id || warning.id_driver}-${dateMs}`;

      const notification = {
        id,
        createdAt: dateMs,
        read: false,
        containerId: 'dashboard',
        data: {
          kind: 'driver_warning',
          warningDateMs: dateMs,
          title: `${i18n.t('driver_warning')}: ${warning.motivo || i18n.t('segnalazione')}`,
          text: warning.commento || i18n.t('nessun_commento'),
          driverId: warning.id_driver,
          driverName: driverName || `${i18n.t('driver')} ${warning.id_driver}`,
          warningId: warning.id,
        },
      };

      notificationCenterInstance.add(notification);

      // Save to localStorage for persistence
      if (Array.isArray(notificationCenterInstance.notifications)) {
        saveNotificationsToStorage([...notificationCenterInstance.notifications, notification]);
      } else {
        // fallback: salva almeno questa
        saveNotificationsToStorage([notification]);
      }
    }
  } catch (error) {
    console.error('Error adding driver warning notification to center:', error);
  }
};

export const addEventNotification = (event, driverName = null, vehicleLabel = null) => {
  try {
    if (notificationCenterInstance && typeof notificationCenterInstance.add === 'function') {
      const timestamp = Date.now();
      const id = `event-${event.id}`;

      // Check if exists using internal array if available or just proceed (add handles duplicates usually if ID matches?)
      // notificationCenter uses ID to dedupe? No, we better check.
      const existingNotifications = Array.isArray(notificationCenterInstance.notifications)
        ? notificationCenterInstance.notifications
        : [];

      if (existingNotifications.some((n) => n.id === id)) {
        return; // Skip duplicate
      }

      // Determine title and text based on status
      const typeKey = event.type || 'unknown';

      let statusTitleKey = 'evento';
      let statusMsgKey = '';

      if (event.status === 'viewed') {
        statusTitleKey = 'presa_visione';
        statusMsgKey = 'msg_presa_visione';
      } else if (event.status === 'completed') {
        statusTitleKey = 'evento_completato';
        statusMsgKey = 'msg_evento_completato';
      }

      const title = `${i18n.t(statusTitleKey)}: ${i18n.t(typeKey)}`;

      const addresses =
        `${event.start_address || ''}${event.end_address ? ' → ' + event.end_address : ''}`.trim();
      const textPrefix = statusMsgKey ? i18n.t(statusMsgKey) : '';

      // Combine prefix and addresses
      const text = [textPrefix, addresses].filter(Boolean).join('. ');

      const eventDateMs = event.date ? new Date(event.date).getTime() : timestamp;

      const notification = {
        id: id + '-' + event.status,
        createdAt: timestamp,
        read: false,
        containerId: 'dashboard',
        data: {
          kind: 'event',
          eventType: typeKey,
          eventDateMs,
          title,
          text,
          eventId: event.id,
          status: event.status,
          image: event.image || null, // Image URL or base64
          coords: event.coords || null, // {lat, lng}
          driverName,
          vehicleLabel,
        },
      };

      notificationCenterInstance.add(notification);

      if (Array.isArray(notificationCenterInstance.notifications)) {
        saveNotificationsToStorage([...notificationCenterInstance.notifications, notification]);
      } else {
        saveNotificationsToStorage([notification]);
      }
    }
  } catch (error) {
    console.error('Error adding event notification to center:', error);
  }
};

export const addOrderNotification = (order, type, nomeCompleto, orarioEsecuzione) => {
  try {
    if (notificationCenterInstance && typeof notificationCenterInstance.add === 'function') {
      const timestamp = Date.now();
      const id = `order-${order.id}-${timestamp}`;

      let title, text, notificationType;

      if (type === 'created') {
        title = `🆕 ${i18n.t('nuovo_ordine_per')} ${nomeCompleto}`;
        text = `📅 ${orarioEsecuzione}`;
        notificationType = 'order_created';
      } else if (type === 'canceled') {
        title = `🚫 ${i18n.t('ordine_annullato_per')} ${nomeCompleto}`;
        text = `🗓️ ${i18n.t('previsto')}: ${orarioEsecuzione}`;
        notificationType = 'order_canceled';
      } else {
        // fallback
        title = `${i18n.t('ordine_aggiornato_per')} ${nomeCompleto}`;
        text = orarioEsecuzione ? `📅 ${orarioEsecuzione}` : '';
        notificationType = 'order_updated';
      }

      const notification = {
        id: id + '-' + order.status,
        createdAt: timestamp,
        read: false,
        containerId: 'dashboard',
        data: {
          kind: 'order',
          orderType: notificationType,
          title,
          text,
          orderId: order.id,
          passengerName: nomeCompleto,
          executionTime: orarioEsecuzione,
          orderStatus: order.status,
        },
      };

      notificationCenterInstance.add(notification);

      // Save to localStorage for persistence
      if (Array.isArray(notificationCenterInstance.notifications)) {
        saveNotificationsToStorage([...notificationCenterInstance.notifications, notification]);
      } else {
        // fallback: salva almeno questa
        saveNotificationsToStorage([notification]);
      }
    }
  } catch (error) {
    console.error('Error adding order notification to center:', error);
  }
};

/**
 * Dispatch invoice payment deadline notifications to the notification center.
 * items: Array of { key, label, totale, stimaDate (ISO), urgencyLevel, daysRemaining }
 */
export const addInvoiceScadenzaNotifications = (items) => {
  try {
    if (!notificationCenterInstance || typeof notificationCenterInstance.add !== 'function') return;
    if (!Array.isArray(items) || items.length === 0) return;

    const dismissed = new Set(loadDismissedNotificationIds());
    const existing = new Set(
      (Array.isArray(notificationCenterInstance.notifications)
        ? notificationCenterInstance.notifications
        : []
      ).map((n) => n.id)
    );

    const toAdd = [];

    items.forEach(({ key, label, totale, stimaDate, urgencyLevel, daysRemaining }) => {
      const id = `invoice-scadenza-${key}`;
      if (dismissed.has(id)) return;
      if (existing.has(id)) return;

      const expirationDateMs = new Date(stimaDate).getTime();
      const absDays = Math.abs(daysRemaining);

      const title =
        urgencyLevel === 'expired' ? `Da sollecitare: ${label}` : `Prossima scadenza: ${label}`;

      let text;
      if (urgencyLevel === 'expired') {
        text = `Scaduta da ${absDays} giorn${absDays === 1 ? 'o' : 'i'} • € ${Number(totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;
      } else if (daysRemaining === 0) {
        text = `Scade oggi • € ${Number(totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;
      } else {
        text = `Scade tra ${daysRemaining} giorn${daysRemaining === 1 ? 'o' : 'i'} • € ${Number(totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;
      }

      toAdd.push({
        id,
        createdAt: Date.now(),
        read: false,
        containerId: 'dashboard',
        data: {
          kind: 'scadenza',
          scadenzaType: 'invoice',
          urgencyLevel,
          title,
          text,
          invoiceKey: key,
          label,
          importo: totale,
          expirationDate: expirationDateMs,
          daysRemaining,
        },
      });
      existing.add(id);
    });

    toAdd.forEach((n) => notificationCenterInstance.add(n));

    if (toAdd.length > 0 && Array.isArray(notificationCenterInstance.notifications)) {
      saveNotificationsToStorage([...notificationCenterInstance.notifications, ...toAdd]);
    }
  } catch (err) {
    console.error('Error adding invoice scadenza notifications:', err);
  }
};

export const addVehicleExpirationNotification = (
  vehicle,
  expirationType,
  expirationDate,
  daysRemaining
) => {
  try {
    if (notificationCenterInstance && typeof notificationCenterInstance.add === 'function') {
      const timestamp = Date.now();
      const expirationDateMs =
        expirationDate instanceof Date
          ? expirationDate.getTime()
          : new Date(expirationDate).getTime();

      // Create unique ID to avoid duplicates
      const id = `vehicle-expiration-${vehicle.id}-${expirationType}-${expirationDateMs}`;

      // Check if this notification already exists
      const existingNotifications = Array.isArray(notificationCenterInstance.notifications)
        ? notificationCenterInstance.notifications
        : [];

      if (existingNotifications.some((n) => n.id === id)) {
        return; // Skip duplicate
      }

      // Determine urgency level and styling
      let urgencyLevel, icon, titleKey;
      if (daysRemaining < 0) {
        urgencyLevel = 'expired';
        icon = '⚠️';
        titleKey = 'scadenza_veicolo';
      } else if (daysRemaining <= 7) {
        urgencyLevel = 'urgent';
        icon = '🔶';
        titleKey = 'scadenza_imminente';
      } else if (daysRemaining <= 30) {
        urgencyLevel = 'warning';
        icon = '⚡';
        titleKey = 'scadenza_prossima';
      }

      // Get translated title with vehicle plate
      const title = `${i18n.t(titleKey)}: ${vehicle.targa || vehicle.label}`;

      // Format expiration type text
      const expirationTypeText = i18n.t(expirationType);

      // Format text based on days remaining
      let text;
      if (daysRemaining < 0) {
        text = `${expirationTypeText} ${i18n.t('scaduta_da_giorni', { days: Math.abs(daysRemaining) })}`;
      } else if (daysRemaining === 0) {
        text = `${expirationTypeText} ${i18n.t('scade_oggi')}!`;
      } else if (daysRemaining === 1) {
        text = `${expirationTypeText} ${i18n.t('scade_domani')}`;
      } else {
        text = `${expirationTypeText} ${i18n.t('scade_tra_giorni', { days: daysRemaining })}`;
      }

      const notification = {
        id,
        createdAt: timestamp,
        read: false,
        containerId: 'dashboard',
        data: {
          kind: 'scadenza',
          urgencyLevel,
          title,
          text,
          vehicleId: vehicle.id,
          vehiclePlate: vehicle.targa,
          vehicleLabel: vehicle.label,
          expirationType,
          expirationDate: expirationDateMs,
          daysRemaining,
        },
      };

      notificationCenterInstance.add(notification);

      // Save to localStorage for persistence
      if (Array.isArray(notificationCenterInstance.notifications)) {
        saveNotificationsToStorage([...notificationCenterInstance.notifications, notification]);
      } else {
        saveNotificationsToStorage([notification]);
      }
    }
  } catch (error) {
    console.error('Error adding vehicle expiration notification to center:', error);
  }
};

/**
 * Notifica di scadenza per un corso/certificazione dell'autista. Stessa forma
 * di `addVehicleExpirationNotification` (kind `scadenza`), così il centro
 * notifiche la mostra nella tab "Scadenze" senza modifiche.
 */
export const addDriverCourseExpirationNotification = (
  driver,
  course,
  courseLabel,
  expirationDate,
  daysRemaining
) => {
  try {
    if (!notificationCenterInstance || typeof notificationCenterInstance.add !== 'function') return;

    const timestamp = Date.now();
    const expirationDateMs =
      expirationDate instanceof Date
        ? expirationDate.getTime()
        : new Date(expirationDate).getTime();

    const id = `driver-course-expiration-${driver.id}-${course.id || course.type}-${expirationDateMs}`;

    const existingNotifications = Array.isArray(notificationCenterInstance.notifications)
      ? notificationCenterInstance.notifications
      : [];
    if (existingNotifications.some((n) => n.id === id)) return; // duplicato

    let urgencyLevel, titleKey;
    if (daysRemaining < 0) {
      urgencyLevel = 'expired';
      titleKey = 'scadenza_corso_autista';
    } else if (daysRemaining <= 7) {
      urgencyLevel = 'urgent';
      titleKey = 'scadenza_imminente';
    } else {
      urgencyLevel = 'warning';
      titleKey = 'scadenza_prossima';
    }

    const driverName = `${driver.nome || ''} ${driver.cognome || ''}`.trim() || driver.id;
    const title = `${i18n.t(titleKey)}: ${driverName}`;

    let text;
    if (daysRemaining < 0) {
      text = `${courseLabel} ${i18n.t('scaduta_da_giorni', { days: Math.abs(daysRemaining) })}`;
    } else if (daysRemaining === 0) {
      text = `${courseLabel} ${i18n.t('scade_oggi')}!`;
    } else if (daysRemaining === 1) {
      text = `${courseLabel} ${i18n.t('scade_domani')}`;
    } else {
      text = `${courseLabel} ${i18n.t('scade_tra_giorni', { days: daysRemaining })}`;
    }

    const notification = {
      id,
      createdAt: timestamp,
      read: false,
      containerId: 'dashboard',
      data: {
        kind: 'scadenza',
        urgencyLevel,
        title,
        text,
        driverId: driver.id,
        driverName,
        scadenzaType: 'driver_course',
        courseType: course.type,
        label: courseLabel,
        expirationDate: expirationDateMs,
        daysRemaining,
      },
    };

    notificationCenterInstance.add(notification);

    if (Array.isArray(notificationCenterInstance.notifications)) {
      saveNotificationsToStorage([...notificationCenterInstance.notifications, notification]);
    } else {
      saveNotificationsToStorage([notification]);
    }
  } catch (error) {
    console.error('Error adding driver course expiration notification to center:', error);
  }
};

/**
 * Notifica per una riga di `fleet_deadlines` (scadenze pre-calcolate dal job).
 *
 * Copre le categorie che il controllo lato client non vede: le manutenzioni
 * ricorrenti (`next_due_date`) e i permessi/ZTL. Revisione, assicurazione, bollo
 * e corsi restano ai due dispatcher sopra, che hanno in mano l'anagrafica e
 * producono un testo più ricco — qui si arriva solo dove quelli non arrivano,
 * così non si sdoppia nulla.
 *
 * @param {{type, category, vehicle_id, targa, subject, due_date, expired}} row
 * @param {number} daysRemaining  negativo = già scaduta
 */
export const addFleetDeadlineNotification = (row, daysRemaining) => {
  try {
    if (!notificationCenterInstance || typeof notificationCenterInstance.add !== 'function') return;
    if (!row?.due_date) return;

    const expirationDateMs = new Date(row.due_date).getTime();
    if (Number.isNaN(expirationDateMs)) return;

    // La chiave include la categoria: una manutenzione e un permesso in scadenza
    // lo stesso giorno sullo stesso mezzo restano due avvisi distinti.
    const id = `fleet-deadline-${row.category}-${row.vehicle_id || row.driver_id || 'x'}-${row.type}-${expirationDateMs}`;

    const existing = Array.isArray(notificationCenterInstance.notifications)
      ? notificationCenterInstance.notifications
      : [];
    if (existing.some((n) => n.id === id)) return;

    let urgencyLevel, titleKey;
    if (daysRemaining < 0) {
      urgencyLevel = 'expired';
      titleKey = 'scadenza_veicolo';
    } else if (daysRemaining <= 7) {
      urgencyLevel = 'urgent';
      titleKey = 'scadenza_imminente';
    } else {
      urgencyLevel = 'warning';
      titleKey = 'scadenza_prossima';
    }

    const subject = row.targa || row.subject || row.vehicle_id || '';
    const label = i18n.t(`maintenance_${row.type}`, {
      defaultValue: i18n.t(row.type, { defaultValue: row.type }),
    });

    let text;
    if (daysRemaining < 0) {
      text = `${label} ${i18n.t('scaduta_da_giorni', { days: Math.abs(daysRemaining) })}`;
    } else if (daysRemaining === 0) {
      text = `${label} ${i18n.t('scade_oggi')}!`;
    } else if (daysRemaining === 1) {
      text = `${label} ${i18n.t('scade_domani')}`;
    } else {
      text = `${label} ${i18n.t('scade_tra_giorni', { days: daysRemaining })}`;
    }

    const notification = {
      id,
      createdAt: Date.now(),
      read: false,
      containerId: 'dashboard',
      data: {
        kind: 'scadenza',
        urgencyLevel,
        title: `${i18n.t(titleKey)}: ${subject}`,
        text,
        vehicleId: row.vehicle_id || null,
        vehiclePlate: row.targa || null,
        driverId: row.driver_id || null,
        scadenzaType: row.category,
        expirationType: row.type,
        expirationDate: expirationDateMs,
        daysRemaining,
      },
    };

    notificationCenterInstance.add(notification);

    if (Array.isArray(notificationCenterInstance.notifications)) {
      saveNotificationsToStorage([...notificationCenterInstance.notifications, notification]);
    } else {
      saveNotificationsToStorage([notification]);
    }
  } catch (error) {
    console.error('Error adding fleet deadline notification to center:', error);
  }
};
